import React from "react";

/**
 * Meditation scene background for the sign-in page.
 * Layers a still image with animated star/aurora/particle overlays
 * so the sky and astral field feel alive and 3D.
 */

// Layered star fields for parallax / depth. Each layer twinkles + drifts.
// far = tiny, dim, slow.  mid = medium.  near = larger, brighter, faster twinkle.
const makeStars = (count, seed, sizeMin, sizeMax) =>
  Array.from({ length: count }).map((_, i) => {
    const s = (seed + i) * 9301 + 49297;
    const rand1 = ((s % 233280) / 233280);
    const rand2 = (((s * 7) % 233280) / 233280);
    const rand3 = (((s * 13) % 233280) / 233280);
    const rand4 = (((s * 19) % 233280) / 233280);
    return {
      id: `${seed}-${i}`,
      x: rand1 * 100,
      y: rand2 * 65, // stars in upper 65% (sky area)
      size: sizeMin + rand3 * (sizeMax - sizeMin),
      delay: rand4 * 8,
      duration: 2 + rand1 * 5,
    };
  });

const FAR_STARS = makeStars(120, 1, 0.6, 1.4);
const MID_STARS = makeStars(60, 2, 1.4, 2.4);
const NEAR_STARS = makeStars(30, 3, 2.4, 3.6);

const SHOOTING = [
  { top: "10%", left: "8%", delay: "0s", duration: "6s" },
  { top: "22%", left: "55%", delay: "3s", duration: "7s" },
  { top: "6%", left: "78%", delay: "5.5s", duration: "5s" },
  { top: "16%", left: "35%", delay: "8s", duration: "6.5s" },
  { top: "30%", left: "70%", delay: "11s", duration: "5.5s" },
];

export const MeditationSceneBackground = () => {
  return (
    <div
      className="absolute inset-0 overflow-hidden"
      aria-hidden="true"
      data-testid="meditation-scene-background"
    >
      {/* Base scene images — slow crossfade between three scenes */}
      <div className="absolute inset-0 scene-breathe">
        {["/aurora-scene.jpg", "/aurora-scene-2.jpg", "/aurora-scene-3.jpg"].map((src, i) => (
          <div
            key={src}
            className="absolute inset-0 bg-no-repeat bg-center bg-cover scene-cross"
            style={{ backgroundImage: `url(${src})`, animationDelay: `${i * 9}s` }}
          />
        ))}
      </div>

      {/* Nebula/galaxy drift — subtle parallax over the milky way */}
      <div className="meditation-nebula" />

      {/* Aurora shimmer bands over the sky */}
      <div className="meditation-aurora meditation-aurora-1" />
      <div className="meditation-aurora meditation-aurora-2" />
      <div className="meditation-aurora meditation-aurora-3" />

      {/* Star field — 3 depth layers each drifting at a different speed */}
      <div className="star-layer star-layer-far">
        {FAR_STARS.map((s) => (
          <span
            key={s.id}
            className="meditation-star"
            style={{
              top: `${s.y}%`,
              left: `${s.x}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.duration}s`,
              opacity: 0.7,
            }}
          />
        ))}
      </div>
      <div className="star-layer star-layer-mid">
        {MID_STARS.map((s) => (
          <span
            key={s.id}
            className="meditation-star star-bright"
            style={{
              top: `${s.y}%`,
              left: `${s.x}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.duration}s`,
            }}
          />
        ))}
      </div>
      <div className="star-layer star-layer-near">
        {NEAR_STARS.map((s) => (
          <span
            key={s.id}
            className="meditation-star star-brightest"
            style={{
              top: `${s.y}%`,
              left: `${s.x}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.duration}s`,
            }}
          />
        ))}
      </div>

      {/* Shooting stars */}
      {SHOOTING.map((s, i) => (
        <span
          key={`shoot-${i}`}
          className="meditation-shooting"
          style={{
            top: s.top,
            left: s.left,
            animationDelay: s.delay,
            animationDuration: s.duration,
          }}
        />
      ))}

      {/* Orb glow */}
      <div className="meditation-orb-glow" />

      {/* Vignettes */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-[#03040780] to-[#030407]" />
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-[#030407cc] via-transparent to-transparent" />
    </div>
  );
};

export default MeditationSceneBackground;
